/**
 * ArbitrationActions Component
 *
 * Action panel for dispute arbitration — full-width action rows with the
 * description visible inline (no tooltip hunting). Grouped by category.
 *
 * Open/Closed Principle: Add new actions without modifying existing code
 */

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  ButtonBase,
  useTheme,
  alpha
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HandshakeIcon from '@mui/icons-material/Handshake';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import BlockIcon from '@mui/icons-material/Block';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import LockIcon from '@mui/icons-material/Lock';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReplayIcon from '@mui/icons-material/Replay';
import GavelIcon from '@mui/icons-material/Gavel';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SearchIcon from '@mui/icons-material/Search';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import {
  ArbitrationAction,
  ActionLabels,
  DisputeType,
  DisputeStatus
} from '../../services/arbitrationService';

interface ArbitrationActionsProps {
  availableActions: ArbitrationAction[];
  disputeType: DisputeType;
  disputeStatus: DisputeStatus;
  onAction: (action: ArbitrationAction) => void;
  loading?: boolean;
  disabled?: boolean;
  /** بدون قاب کارت — برای استفاده داخل تب سایدبار */
  embedded?: boolean;
}

// Action configurations
interface ActionConfig {
  icon: React.ReactNode;
  color: 'success' | 'error' | 'warning' | 'info' | 'primary';
  description: string;
  confirmRequired: boolean;
  showFor: DisputeType[];
  showForStatus: DisputeStatus[];
}

const ActionConfigs: Record<ArbitrationAction, ActionConfig> = {
  [ArbitrationAction.APPROVE]: {
    icon: <CheckCircleIcon />,
    color: 'success',
    description: 'تأیید اختلاف و ایجاد چت داوری',
    confirmRequired: true,
    showFor: [DisputeType.ORDER, DisputeType.ORDER_ITEM, DisputeType.INSPECTION],
    showForStatus: [DisputeStatus.PENDING]
  },
  [ArbitrationAction.REJECT]: {
    icon: <CancelIcon />,
    color: 'error',
    description: 'رد درخواست اختلاف',
    confirmRequired: true,
    showFor: [DisputeType.ORDER, DisputeType.ORDER_ITEM, DisputeType.INSPECTION],
    showForStatus: [DisputeStatus.PENDING]
  },
  [ArbitrationAction.RESOLVE]: {
    icon: <HandshakeIcon />,
    color: 'success',
    description: 'حل اختلاف و بستن پرونده',
    confirmRequired: true,
    showFor: [DisputeType.ORDER, DisputeType.ORDER_ITEM, DisputeType.INSPECTION],
    showForStatus: [DisputeStatus.APPROVED, DisputeStatus.INVESTIGATING]
  },
  [ArbitrationAction.REFUND_ORDER]: {
    icon: <MonetizationOnIcon />,
    color: 'info',
    description: 'بازپرداخت کل سفارش',
    confirmRequired: true,
    showFor: [DisputeType.ORDER],
    showForStatus: [DisputeStatus.APPROVED, DisputeStatus.INVESTIGATING]
  },
  [ArbitrationAction.REFUND_ITEM]: {
    icon: <MonetizationOnIcon />,
    color: 'info',
    description: 'بازپرداخت آیتم به خریدار',
    confirmRequired: true,
    showFor: [DisputeType.ORDER_ITEM, DisputeType.INSPECTION],
    showForStatus: [DisputeStatus.APPROVED, DisputeStatus.INVESTIGATING]
  },
  [ArbitrationAction.SEND_MESSAGE]: {
    icon: null,
    color: 'primary',
    description: 'ارسال پیام در چت',
    confirmRequired: false,
    showFor: [DisputeType.ORDER, DisputeType.ORDER_ITEM, DisputeType.INSPECTION],
    showForStatus: [DisputeStatus.APPROVED, DisputeStatus.INVESTIGATING]
  },
  [ArbitrationAction.CLOSE_CHAT]: {
    icon: <LockIcon />,
    color: 'warning',
    description: 'بستن چت داوری',
    confirmRequired: true,
    showFor: [DisputeType.ORDER, DisputeType.ORDER_ITEM, DisputeType.INSPECTION],
    showForStatus: [DisputeStatus.APPROVED, DisputeStatus.INVESTIGATING]
  },
  [ArbitrationAction.CHANGE_ITEM_STATUS]: {
    icon: <SwapHorizIcon />,
    color: 'primary',
    description: 'تغییر وضعیت آیتم سفارش',
    confirmRequired: true,
    showFor: [DisputeType.ORDER_ITEM],
    showForStatus: [DisputeStatus.APPROVED, DisputeStatus.INVESTIGATING]
  },
  [ArbitrationAction.FORCE_DELIVER]: {
    icon: <LocalShippingIcon />,
    color: 'success',
    description: 'تأیید تحویل اجباری توسط داور',
    confirmRequired: true,
    showFor: [DisputeType.ORDER_ITEM],
    showForStatus: [DisputeStatus.APPROVED, DisputeStatus.INVESTIGATING]
  },
  [ArbitrationAction.REQUEST_REDELIVERY]: {
    icon: <ReplayIcon />,
    color: 'warning',
    description: 'درخواست تحویل مجدد از فروشنده',
    confirmRequired: true,
    showFor: [DisputeType.ORDER_ITEM],
    showForStatus: [DisputeStatus.APPROVED, DisputeStatus.INVESTIGATING]
  },
  // Inspection actions
  [ArbitrationAction.REFUND_INSPECTION_FEE]: {
    icon: <ReceiptLongIcon />,
    color: 'info',
    description: 'بازپرداخت هزینه بازرسی و ارسال به کیف پول خریدار',
    confirmRequired: true,
    showFor: [DisputeType.INSPECTION],
    showForStatus: [DisputeStatus.APPROVED, DisputeStatus.INVESTIGATING]
  },
  [ArbitrationAction.OVERRIDE_INSPECTION]: {
    icon: <SearchIcon />,
    color: 'warning',
    description: 'تغییر نتیجه بازرسی توسط ادمین',
    confirmRequired: true,
    showFor: [DisputeType.INSPECTION],
    showForStatus: [DisputeStatus.APPROVED, DisputeStatus.INVESTIGATING]
  },
  [ArbitrationAction.PENALIZE_INSPECTOR]: {
    icon: <ReportProblemIcon />,
    color: 'error',
    description: 'اعمال جریمه امتیازی به بازرس',
    confirmRequired: true,
    showFor: [DisputeType.INSPECTION],
    showForStatus: [DisputeStatus.APPROVED, DisputeStatus.INVESTIGATING]
  }
};

// Group actions by category
const ActionGroups: Record<string, { label: string; actions: ArbitrationAction[] }> = {
  primary: {
    label: 'اقدامات اصلی',
    actions: [ArbitrationAction.APPROVE, ArbitrationAction.REJECT]
  },
  resolution: {
    label: 'حل اختلاف',
    actions: [ArbitrationAction.RESOLVE, ArbitrationAction.REFUND_ORDER, ArbitrationAction.REFUND_ITEM]
  },
  orderItem: {
    label: 'مدیریت آیتم سفارش',
    actions: [ArbitrationAction.CHANGE_ITEM_STATUS, ArbitrationAction.FORCE_DELIVER, ArbitrationAction.REQUEST_REDELIVERY]
  },
  inspection: {
    label: 'اقدامات بازرسی',
    actions: [ArbitrationAction.REFUND_INSPECTION_FEE, ArbitrationAction.OVERRIDE_INSPECTION, ArbitrationAction.PENALIZE_INSPECTOR]
  },
  management: {
    label: 'مدیریت چت',
    actions: [ArbitrationAction.CLOSE_CHAT]
  }
};

/**
 * Action Row — full-width clickable row with the description visible inline.
 */
const ActionRow: React.FC<{
  action: ArbitrationAction;
  config: ActionConfig;
  onClick: () => void;
  disabled?: boolean;
}> = ({ action, config, onClick, disabled }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  if (!config.icon) return null;

  const hex = theme.palette[config.color].main;

  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.25,
        py: 1,
        borderRadius: 2,
        border: `1px solid ${alpha(hex, isDark ? 0.35 : 0.25)}`,
        bgcolor: alpha(hex, isDark ? 0.08 : 0.04),
        textAlign: 'start',
        justifyContent: 'flex-start',
        transition: 'background-color .15s, transform .1s',
        '&:hover': { bgcolor: alpha(hex, isDark ? 0.16 : 0.1) },
        '&:active': { transform: 'scale(0.99)' },
        '&.Mui-disabled': { opacity: 0.45 }
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(hex, isDark ? 0.25 : 0.14),
          color: hex,
          flexShrink: 0,
          '& svg': { fontSize: 19 }
        }}
      >
        {config.icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
          {ActionLabels[action]}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', fontSize: 10.5, lineHeight: 1.5 }}
        >
          {config.description}
        </Typography>
      </Box>
      <KeyboardArrowLeftIcon sx={{ fontSize: 17, color: alpha(hex, 0.7), flexShrink: 0 }} />
    </ButtonBase>
  );
};

const ArbitrationActions: React.FC<ArbitrationActionsProps> = ({
  availableActions,
  disputeType,
  disputeStatus,
  onAction,
  loading = false,
  disabled = false,
  embedded = false
}) => {
  // Filter actions based on type and status
  const filterActions = (actions: ArbitrationAction[]): ArbitrationAction[] => {
    return actions.filter(action => {
      // Check if action is available from server
      if (!availableActions.includes(action)) return false;

      const config = ActionConfigs[action];
      if (!config) return false;

      // Check type and status
      return (
        config.showFor.includes(disputeType) &&
        config.showForStatus.includes(disputeStatus)
      );
    });
  };

  // Check if dispute is in a final state
  const isClosed = [DisputeStatus.RESOLVED, DisputeStatus.REJECTED, DisputeStatus.CLOSED].includes(disputeStatus);

  if (isClosed) {
    const closedAlert = (
      <Alert severity="info" icon={<LockIcon />} sx={{ borderRadius: 2 }}>
        این اختلاف بسته شده است و امکان انجام اقدام جدید وجود ندارد.
      </Alert>
    );
    if (embedded) return <Box sx={{ p: 1.5 }}>{closedAlert}</Box>;
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, bgcolor: 'background.paper' }}>
        {closedAlert}
      </Paper>
    );
  }

  const content = (
    <>
      {/* Header — در حالت embedded خودِ تب عنوان است */}
      {!embedded && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
          <GavelIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
          <Typography variant="subtitle2" fontWeight={800}>
            اقدامات داوری
          </Typography>
        </Box>
      )}

      {/* Action Groups */}
      {Object.entries(ActionGroups).map(([groupKey, group]) => {
        const filteredActions = filterActions(group.actions);
        if (filteredActions.length === 0) return null;

        return (
          <Box key={groupKey} sx={{ mb: 1.5, '&:last-of-type': { mb: 0 } }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 0.6, display: 'block', fontSize: '0.68rem', fontWeight: 700 }}
            >
              {group.label}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {filteredActions.map((action) => {
                const config = ActionConfigs[action];
                return (
                  <ActionRow
                    key={action}
                    action={action}
                    config={config}
                    onClick={() => onAction(action)}
                    disabled={disabled || loading}
                  />
                );
              })}
            </Box>
          </Box>
        );
      })}

      {/* No Actions Available */}
      {availableActions.length === 0 && (
        <Alert severity="warning" sx={{ mt: 1, fontSize: '0.8rem', borderRadius: 2 }}>
          در حال حاضر اقدامی برای این اختلاف در دسترس نیست.
        </Alert>
      )}
    </>
  );

  if (embedded) {
    return <Box sx={{ p: 1.5 }}>{content}</Box>;
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2.5, bgcolor: 'background.paper' }}>
      {content}
    </Paper>
  );
};

export default ArbitrationActions;
